import { useMemo } from "react";
import { useTable } from "react-table";
import primaryComponents from "../../../primaryComponents";
import styles from "./styles.module.scss";
import { faMultiply } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const useJournalLineTable = ({
  journalEntryDetails,
  setJournalEntryDetails = () => {},
  chartAccounts = [],
  taxOptions,
  ...validationParams
}) => {
  const {
    control,
    register,
    trigger,
    formState: { errors },
    setValue,
  } = validationParams;

  const columns = useMemo(
    () => [
      {
        Header: "DESCRIPTION",
        accessor: "description",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.InputFieldV2
            control={control}
            name={`lines.${row?.index}.description`}
            register={register}
            placeholder={"Enter description"}
            classNames={`white ${styles.inputField2}`}
            value={value}
            errorMessage={errors?.lines?.[row?.index]?.description}
            floatError={false}
            onKeyUp={() =>
              errors?.lines?.[row?.index]?.description &&
              trigger(`lines.${row?.index}.description`)
            }
            onChange={({ target: { value } }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].description = value;

              setJournalEntryDetails((prevState) => ({
                ...prevState,
                lines: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: "ACCOUNT",
        accessor: "chart_account_id",
        Cell: ({ row, cell: { value } }) => (
          <primaryComponents.SelectFieldV2
            control={control}
            name={`lines.${row?.index}.chart_account_id`}
            classNames={`white ${styles.inputField}`}
            errorMessage={errors?.lines?.[row?.index]?.chart_account_id}
            floatError={false}
            isRequired
            options={chartAccounts}
            value={
              chartAccounts?.find(
                (chartAccount) => `${chartAccount?.id}` === `${value}`,
              )?.account_label || "Select Account"
            }
            nameKey={"account_label"}
            idKey={"id"}
            onChange={(value) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].chart_account_id = value;

              setJournalEntryDetails((prevState) => ({
                ...prevState,
                lines: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: "TAX RATE",
        accessor: "tax_rate",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.VatPercentField
            name={`lines.${row?.index}.tax_rate`}
            control={control}
            classNames={`white ${styles.inputField}`}
            value={value}
            onChange={({ percentValue }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].tax_rate =
                percentValue === "" ? 0 : percentValue;

              setJournalEntryDetails((prevState) => ({
                ...prevState,
                lines: updatedItems,
              }));
            }}
            isDisabled={journalEntryDetails.tax_type === taxOptions.no_tax}
            onKeyUp={() =>
              errors?.lines?.[row?.index]?.tax_rate &&
              trigger(`lines.${row?.index}.tax_rate`)
            }
            floatError={false}
            type="number"
            min="0"
            max="100"
            step="0.01"
            errorMessage={errors?.lines?.[row?.index]?.tax_rate}
            dividedValue={
              instance?.data[row?.index]?.credit_amount ||
              0 + instance?.data[row?.index]?.debit_amount ||
              0
            }
            vatInclusive={
              journalEntryDetails.tax_type === taxOptions.tax_inclusive
            }
          />
        ),
      },
      {
        Header: "DEBIT",
        accessor: "debit_amount",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.InputFieldV2
            type="number"
            control={control}
            name={`lines.${row?.index}.debit_amount`}
            register={register}
            placeholder={"Enter Debit Amount"}
            classNames={`white ${styles.inputField}`}
            value={value}
            errorMessage={errors?.lines?.[row?.index]?.debit_amount}
            floatError={false}
            onKeyUp={() => {
              errors?.lines?.[row?.index]?.debit_amount &&
                trigger(`lines.${row?.index}.debit_amount`);
              errors?.lines?.[row?.index]?.credit_amount &&
                trigger(`lines.${row?.index}.credit_amount`);
            }}
            onChange={({ target: { value } }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].debit_amount = value;
              updatedItems[row.index].credit_amount = "";

              setJournalEntryDetails((prevState) => ({
                ...prevState,
                lines: updatedItems,
              }));

              setValue(`lines.${row?.index}.credit_amount`, "", {
                shouldDirty: false,
                shouldValidate: false,
                shouldTouch: false,
              });
            }}
          />
        ),
      },
      {
        Header: "CREDIT",
        accessor: "credit_amount",
        Cell: ({ cell: { value }, row }) => (
          <div className="d-flex align-items-center">
            <primaryComponents.InputFieldV2
              type="number"
              control={control}
              name={`lines.${row?.index}.credit_amount`}
              register={register}
              placeholder={"Enter Credit Amount"}
              classNames={`white ${styles.inputField}`}
              value={value}
              errorMessage={errors?.lines?.[row?.index]?.credit_amount}
              floatError={false}
              onKeyUp={() => {
                errors?.lines?.[row?.index]?.debit_amount &&
                  trigger(`lines.${row?.index}.debit_amount`);
                errors?.lines?.[row?.index]?.credit_amount &&
                  trigger(`lines.${row?.index}.credit_amount`);
              }}
              onChange={({ target: { value } }) => {
                const updatedItems = [...instance?.data];
                updatedItems[row.index].credit_amount = value;
                updatedItems[row.index].debit_amount = "";

                setJournalEntryDetails((prevState) => ({
                  ...prevState,
                  lines: updatedItems,
                }));

                setValue(`lines.${row?.index}.debit_amount`, "", {
                  shouldDirty: false,
                  shouldValidate: false,
                  shouldTouch: false,
                });
              }}
            />
            <span
              className="mr-2 ml-2 cursor-pointer"
              style={{ color: "#9091b3" }}
              onClick={() => deleteRow(row?.index)}
            >
              <FontAwesomeIcon
                icon={faMultiply}
                style={{ width: "17px", height: "17px" }}
              />
            </span>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [journalEntryDetails?.lines?.length, journalEntryDetails.tax_type, errors],
  );

  const instance = useTable({
    columns,
    data: journalEntryDetails?.lines || [],
  });

  const deleteRow = (index) => {
    const updatedItems = [...instance?.data];
    updatedItems.splice(index, 1);

    setJournalEntryDetails((prevState) => ({
      ...prevState,
      lines: updatedItems,
    }));

    setValue("lines", updatedItems);
  };

  return {
    instance,
  };
};
export default useJournalLineTable;
